/**
 * The pure half of the Outgoing mail (SMTP) section on
 * Admin › Settings › Integrations. Foundations 2 (Users rail), B2d-T3 review
 * fixes (R5, B2dT3-10).
 *
 * These are here, and not inline in `integrations.tsx`, for one reason: they
 * are the two decisions the section got wrong, and a decision that can be tested
 * without a router and a query client is a decision that gets tested.
 *
 *   R5 — "no relay configured" is a 404 and nothing else. The settings query
 *        used to catch EVERY error and return null, so a 500 or a 403 rendered
 *        the same blank form and the same "Platform default" badge as a genuine
 *        404. The admin then typed a host, clicked Save, and the PUT replaced
 *        the whole config object from an empty form — discarding the username,
 *        from name and reply-to they were never shown.
 *
 *   B2dT3-10 — the port is typed, one character at a time. `Number('')` is 0 and
 *        `Number('5 87')` is NaN, and both used to go straight into the form
 *        state and out in the payload, where the API answered a raw Zod error
 *        for a field the admin was in the middle of editing.
 */

/** The API client attaches the HTTP status to the error it throws. */
export function errorStatus(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * True only for the one answer that MEANS "this organization has no relay of
 * its own": 404. Every other failure is a failure, and must be shown as one —
 * an empty form whose Save overwrites the stored configuration is worse than an
 * error message.
 */
export function isRelayNotConfigured(err: unknown): boolean {
  return errorStatus(err) === 404;
}

/** What the SMTP port field may hold while it is being typed. */
export interface PortParseResult {
  /** The number to send, or null when there is nothing valid to send yet. */
  value: number | null;
  /** A message for the admin, or null when the field is fine. */
  error: string | null;
}

/**
 * `TenantSmtpSettingWriteSchema.port` is `z.number().int().min(1).max(65535)`.
 * This says the same thing in the same words the admin would use, BEFORE the
 * request is made, so a half-typed port is a hint on the field rather than a
 * 400 with a Zod body in a toast.
 */
export function parsePortInput(raw: string): PortParseResult {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { value: null, error: 'Port is required' };
  }
  if (!/^\d+$/.test(trimmed)) {
    return { value: null, error: 'Port must be a whole number' };
  }
  const value = Number(trimmed);
  if (value < 1 || value > 65535) {
    return { value: null, error: 'Port must be between 1 and 65535' };
  }
  return { value, error: null };
}
